/* eslint-disable max-len */
/* eslint-disable max-lines */
import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    HStack, Radio, RadioGroup, Skeleton, Stack,
    VStack,
} from '@chakra-ui/react';
import { Controller, FormProvider } from 'react-hook-form';
import { Suspense, useState } from 'react';
import { DatePickerField } from '@acp/core-ui';
import { AccessIssueEntryMethodLabel, EntryMethodPreference } from '@acp/shared';
import TinyMCEEditorWithImageUpload from '../../../../components/tinymce-editor/image-upload';
import { EntryMethodFormValues, ReferenceValue } from './form-schema';
import { useEntryMethodForm } from '../../hooks/use-entry-method-form';

const TINYMCE_CONTAINER_HEIGHT = 300;

function EntryMethodFormSkeleton() {
    return (
        <VStack align="left" spacing={6} p={4}>
            <Box>
                <FormLabel>Entry method preference</FormLabel>
                <Skeleton p={6} h={2} w="full" />
            </Box>
            <Box>
                <FormLabel>Add comments</FormLabel>
                <Skeleton p={6} h={TINYMCE_CONTAINER_HEIGHT} w="full" />
            </Box>
        </VStack>
    );
}

export function UpdateEntryMethodForm({
    propertyId,
    entryMethodField,
    onEntryMethodUpdated,
    confirmButtonText,
}: {
    propertyId: number;
    confirmButtonText?: string;
    entryMethodField?: AccessIssueEntryMethodLabel;
    onEntryMethodUpdated: (notes?: string) => void;
}) {
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const {
        entryMethodForm,
        isLoading,
        expDateMessage,
        handleConfirm,
        handleSelectOption,
    } = useEntryMethodForm({ propertyId });
    const {
        control, formState: { isValid }, handleSubmit, getValues,
    } = entryMethodForm;
    const fromDate = new Date(getValues('tempExpireDateSettings.min'));
    const toDate = new Date(getValues('tempExpireDateSettings.max'));

    if (entryMethodField === undefined) {
        return null;
    }

    if (isLoading) {
        return (
            <EntryMethodFormSkeleton />
        );
    }

    function onSubmit(formValues: EntryMethodFormValues) {
        handleConfirm(formValues);
        onEntryMethodUpdated(formValues.comments);
    }

    return (
        <FormProvider {...entryMethodForm}>
            <VStack align="left">
                <VStack align="left" spacing={6}>
                    {entryMethodField === AccessIssueEntryMethodLabel.EnterWithKeyAsEn && (
                        <Controller
                            control={control}
                            name="fieldOption.internalEnPreference"
                            render={({ field, fieldState }) => (
                                <FormControl isInvalid={fieldState.invalid}>
                                    <FormLabel>Entry notification preference</FormLabel>
                                    <RadioGroup
                                        {...field}
                                        isDisabled={field.disabled}
                                        onChange={(value: ReferenceValue) => {
                                            handleSelectOption({
                                                fieldName: EntryMethodPreference.internalEnPreference,
                                                optionValue: value,
                                            });
                                        }}
                                    >
                                        <Stack direction="row" spacing={4}>
                                            <Radio value={ReferenceValue.ALWAYS_ALLOWED}>
                                                Always allow for this property
                                            </Radio>
                                            <Radio value={ReferenceValue.ALLOW_UNTIL_DATE}>
                                                Allow until a set date
                                            </Radio>
                                        </Stack>
                                    </RadioGroup>

                                    {field.value === ReferenceValue.ALLOW_UNTIL_DATE && (
                                        <Box mt={4}>
                                            <DatePickerField
                                                control={control}
                                                name="tempPreferences.internalEnPreferenceExpiresAt"
                                                label="Temporary expiration date"
                                                isRequired={
                                                    field.value === ReferenceValue.ALLOW_UNTIL_DATE
                                                }
                                                allowClear={false}
                                                fromDate={fromDate}
                                                toDate={toDate}
                                            />
                                            <FormHelperText mt={2}>
                                                {expDateMessage}
                                            </FormHelperText>
                                        </Box>
                                    )}

                                    {
                                        fieldState.error && (
                                            <FormErrorMessage>
                                                { fieldState.error.message}
                                            </FormErrorMessage>
                                        )
                                    }
                                </FormControl>
                            )}
                        />
                    )}

                    {entryMethodField === AccessIssueEntryMethodLabel.Appointment && (
                        <Controller
                            control={control}
                            name="fieldOption.occupantAppointmentPreference"
                            render={({ field, fieldState }) => (
                                <FormControl isInvalid={fieldState.invalid}>
                                    <FormLabel>Appoinment preference</FormLabel>
                                    <RadioGroup
                                        {...field}
                                        isDisabled={field.disabled}
                                        onChange={(value: ReferenceValue) => {
                                            handleSelectOption({
                                                fieldName: EntryMethodPreference.occupantAppointmentPreference,
                                                optionValue: value,
                                            });
                                        }}
                                    >
                                        <Stack direction="row" spacing={4}>
                                            <Radio value={ReferenceValue.ALWAYS_ALLOWED}>
                                                Always allow for this property
                                            </Radio>
                                            <Radio value={ReferenceValue.ALLOW_UNTIL_DATE}>
                                                Allow until a set date
                                            </Radio>
                                        </Stack>
                                    </RadioGroup>

                                    {field.value === ReferenceValue.ALLOW_UNTIL_DATE && (
                                        <Box mt={4}>
                                            <DatePickerField
                                                control={control}
                                                name="tempPreferences.occupantAppointmentPreferenceExpiresAt"
                                                label="Temporary expiration date"
                                                isRequired={
                                                    field.value === ReferenceValue.ALLOW_UNTIL_DATE
                                                }
                                                allowClear={false}
                                                fromDate={fromDate}
                                                toDate={toDate}
                                            />
                                            <FormHelperText mt={2}>
                                                {expDateMessage}
                                            </FormHelperText>
                                        </Box>
                                    )}

                                    {
                                        fieldState.error && (
                                            <FormErrorMessage>
                                                { fieldState.error.message}
                                            </FormErrorMessage>
                                        )
                                    }
                                </FormControl>
                            )}
                        />
                    )}

                    {entryMethodField === AccessIssueEntryMethodLabel.EnterWithKeyVacant && (
                        <Controller
                            control={control}
                            name="fieldOption.vacantEntryPreference"
                            render={({ field, fieldState }) => (
                                <FormControl isInvalid={fieldState.invalid}>
                                    <FormLabel>Vacant entry preference</FormLabel>
                                    <RadioGroup
                                        {...field}
                                        isDisabled={field.disabled}
                                        onChange={(value: ReferenceValue) => {
                                            handleSelectOption({
                                                fieldName: EntryMethodPreference.vacantEntryPreference,
                                                optionValue: value,
                                            });
                                        }}
                                    >
                                        <Stack direction="row" spacing={4}>
                                            <Radio value={ReferenceValue.ALWAYS_ALLOWED}>
                                                Always allow for this property
                                            </Radio>
                                            <Radio value={ReferenceValue.ALLOW_UNTIL_DATE}>
                                                Allow until a set date
                                            </Radio>
                                        </Stack>
                                    </RadioGroup>

                                    {field.value === ReferenceValue.ALLOW_UNTIL_DATE && (
                                        <Box mt={4}>
                                            <DatePickerField
                                                control={control}
                                                name="tempPreferences.vacantEntryPreferenceExpiresAt"
                                                label="Temporary expiration date"
                                                isRequired={
                                                    field.value === ReferenceValue.ALLOW_UNTIL_DATE
                                                }
                                                allowClear={false}
                                                fromDate={fromDate}
                                                toDate={toDate}
                                            />
                                            <FormHelperText mt={2}>
                                                {expDateMessage}
                                            </FormHelperText>
                                        </Box>
                                    )}

                                    {
                                        fieldState.error && (
                                            <FormErrorMessage>
                                                { fieldState.error.message}
                                            </FormErrorMessage>
                                        )
                                    }
                                </FormControl>
                            )}
                        />
                    )}

                    <Controller
                        control={control}
                        name="comments"
                        render={({ field, fieldState }) => (
                            <FormControl isInvalid={fieldState.invalid}>
                                <FormLabel>Add comments</FormLabel>
                                <Suspense fallback={<Skeleton height={TINYMCE_CONTAINER_HEIGHT} />}>
                                    <TinyMCEEditorWithImageUpload
                                        containerHeight={TINYMCE_CONTAINER_HEIGHT}
                                        onStartUpload={() => {
                                            setIsUploadingImage(true);
                                        }}
                                        onFinishUpload={() => {
                                            setIsUploadingImage(false);
                                        }}
                                        onEditorChange={field.onChange}
                                        value={field.value}
                                    />
                                </Suspense>
                                {
                                    fieldState.error && (
                                        <FormErrorMessage>
                                            { fieldState.error.message}
                                        </FormErrorMessage>
                                    )
                                }
                            </FormControl>
                        )}
                    />
                </VStack>
                <HStack justifyContent="end" pt={6}>
                    <Button
                        variant="solid"
                        colorScheme="secondary"
                        size="md"
                        onClick={handleSubmit(onSubmit)}
                        isDisabled={isUploadingImage || !isValid}
                    >
                        {confirmButtonText ?? 'Confirm'}
                    </Button>
                </HStack>
            </VStack>
        </FormProvider>
    );
}
